import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Avatar } from "primereact/avatar";
import { Divider } from "primereact/divider";
import { Steps } from "primereact/steps";
import { Timeline } from "primereact/timeline";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../services/store/userSlice";
import { toast } from "react-toastify";
import {
  EssCard,
  CommentThread,
  StatusTag,
  PriorityTag,
} from "./components/helper";
import { STEPS_MODEL } from "./data/constants";
import { supportApi } from "../../services";

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const [ticket, setTicket] = useState(null);
  const [approvalReply, setApprovalReply] = useState("");
  const [respondingApproval, setRespondingApproval] = useState(false);
  const [sla, setSla] = useState({
    slaDueAt: null,
    slaBreach: false,
    slaRemaining: "—",
    slaPct: 0,
  });
  const [comments, setComments] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInputRef = useRef(null);
  const slaTimerRef = useRef(null);

  const stepIndex = {
    New: 0,
    Open: 1,
    "In Progress": 2,
    "On Hold": 3,
    Resolved: 4,
    Closed: 5,
  };

  const loadSla = async (ticketId) => {
    try {
      const res = await supportApi.getTicketSlaStatus(ticketId);
      const d = res.data?.data;
      if (d)
        setSla({
          slaDueAt: d.slaDueAt,
          slaBreach: d.slaBreach,
          slaRemaining: d.slaRemaining,
          slaPct: d.slaPct ?? 0,
        });
    } catch { }
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([
      supportApi.getTicketById(id),
      supportApi.getComments(id),
      supportApi.getActivityLog(id),
      supportApi.getAttachments(id),
    ])
      .then(([t, c, a, att]) => {
        const ticketData = t.data?.data ?? t.data;
        setTicket(ticketData);
        // Seed SLA from ticket data immediately
        if (ticketData)
          setSla({
            slaDueAt: ticketData.slaDueAt,
            slaBreach: ticketData.slaBreach,
            slaRemaining: ticketData.slaRemaining ?? "—",
            slaPct: ticketData.slaPct ?? 0,
          });
        const cms = c.data?.data;
        setComments(Array.isArray(cms) ? cms : []);
        const act = a.data?.data;
        setActivityLog(Array.isArray(act) ? act : []);
        const atts = att.data?.data;
        setAttachments(Array.isArray(atts) ? atts : []);
      })
      .catch(() => toast.error("Failed to load ticket"));

    // Poll SLA every 30 seconds for live countdown
    loadSla(id);
    slaTimerRef.current = setInterval(() => loadSla(id), 30000);
    return () => clearInterval(slaTimerRef.current);
  }, [id]);

  const isWatcher = Array.isArray(ticket?.watchers) && ticket.watchers.includes(currentUser?.id);
  const isAgentOrAdmin = isWatcher;

  // Find a pending approval request directed at the current user
  const pendingApproval = comments.find(
    (c) =>
      c.isApprovalRequest &&
      c.approvalStatus === "pending" &&
      Array.isArray(c.approverIds) &&
      c.approverIds.includes(currentUser?.id),
  );

  const onRespondApproval = async (decision) => {
    if (!pendingApproval) return;
    setRespondingApproval(true);
    try {
      await supportApi.respondApproval(id, pendingApproval.id, { decision, comment: approvalReply });
      setApprovalReply("");
      const [cRes, actRes] = await Promise.all([
        supportApi.getComments(id),
        supportApi.getActivityLog(id),
      ]);
      setComments(Array.isArray(cRes.data?.data) ? cRes.data.data : []);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success(`Approval ${decision} successfully`);
    } catch {
      toast.error("Failed to submit approval response");
    } finally {
      setRespondingApproval(false);
    }
  };

  const canReply = ticket?.status !== "Closed" && (ticket?.createdBy === currentUser?.id || isWatcher);

  const slaColor = sla.slaBreach
    ? "var(--red)"
    : sla.slaPct >= 80
      ? "var(--orange)"
      : "var(--green)";
  const slaBarColor = sla.slaBreach
    ? "#e53935"
    : sla.slaPct >= 80
      ? "#fb8c00"
      : "#22a06b";

  const onSendReply = async () => {
    if (!reply.trim() && pendingFiles.length === 0) return;
    setSending(true);
    try {
      if (reply.trim()) {
        const res = await supportApi.addComment(id, {
          message: reply,
          isInternalNote: false,
        });
        setComments((p) => [...p, res.data?.data ?? res.data]);
      }
      if (pendingFiles.length > 0) {
        await supportApi.uploadAttachment(id, pendingFiles);
        setPendingFiles([]);
      }
      setReply("");
      const attRes = await supportApi.getAttachments(id);
      setAttachments(Array.isArray(attRes.data?.data) ? attRes.data.data : []);
      toast.success("Reply sent");
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const onReopenTicket = async () => {
    try {
      await supportApi.reopenTicket(id);
      const [tRes, actRes] = await Promise.all([
        supportApi.getTicketById(id),
        supportApi.getActivityLog(id),
      ]);
      const updated = tRes.data?.data ?? tRes.data;
      if (updated) setTicket(updated);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Ticket reopened successfully");
    } catch {
      toast.error("Failed to reopen ticket");
    }
  };

  const onCloseTicket = async () => {
    try {
      await supportApi.closeTicket(id);
      const [tRes, actRes] = await Promise.all([
        supportApi.getTicketById(id),
        supportApi.getActivityLog(id),
      ]);
      const updated = tRes.data?.data ?? tRes.data;
      if (updated) setTicket(updated);
      setActivityLog(Array.isArray(actRes.data?.data) ? actRes.data.data : []);
      toast.success("Ticket closed successfully");
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  const onDownload = async (att) => {
    try {
      const res = await supportApi.downloadAttachment(id, att.id);

      const url = window.URL.createObjectURL(res.data);

      const a = document.createElement("a");
      a.href = url;
      a.download = att.fileName;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const onView = async (att) => {
    try {
      const res = await supportApi.downloadAttachment(id, att.id);
      const blob = new Blob([res.data], {
        type: att.mimeType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Preview failed");
    }
  };

  if (!ticket)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>Loading ticket…</div>
    );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Button
          icon="pi pi-arrow-left"
          label="Back"
          outlined
          severity="secondary"
          size="small"
          onClick={() => navigate("/support")}
        />
        <h1 className="ess-page-title" style={{ margin: 0 }}>
          Ticket {ticket.ticketNo}
        </h1>
        <StatusTag value={ticket.status} />
        <PriorityTag value={ticket.priority} />
        {ticket.status === "Resolved" && (ticket.createdBy === currentUser?.id || isWatcher) && (
          <Button
            label="Close Ticket"
            icon="pi pi-lock"
            severity="danger"
            size="small"
            onClick={onCloseTicket}
            style={{ marginLeft: "auto" }}
          />
        )}
        {["Resolved", "Closed"].includes(ticket.status) && isWatcher && (
          <Button
            label="Reopen Ticket"
            icon="pi pi-refresh"
            severity="warning"
            size="small"
            onClick={onReopenTicket}
            style={{ marginLeft: ticket.status === "Resolved" && (ticket.createdBy === currentUser?.id || isWatcher) ? 8 : "auto" }}
          />
        )}
      </div>

      <div className="ess-card" style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--muted)",
            textTransform: "uppercase",
            letterSpacing: ".5px",
            marginBottom: 14,
          }}
        >
          Ticket Progress
        </div>
        <Steps
          model={STEPS_MODEL}
          activeIndex={stepIndex[ticket.status] ?? 0}
        />
      </div>

      <div className="col-detail">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <EssCard title="Ticket Details" icon="pi-file-edit">
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "var(--navy)",
                marginBottom: 12,
              }}
            >
              {ticket.subject}
            </div>
            <p
              style={{
                fontSize: 13,
                color: "#1a2a3a",
                lineHeight: 1.7,
                marginBottom: 14,
              }}
            >
              {ticket.description}
            </p>
            <Divider />
            <div className="meta-grid">
              <div className="meta-item">
                <label>Category</label>
                <span>{ticket.category?.name ?? ticket.category}</span>
              </div>
              <div className="meta-item">
                <label>Sub-Category</label>
                <span>{ticket.subcategory?.name ?? "—"}</span>
              </div>
              <div className="meta-item">
                <label>Raised By</label>
                <span>
                  {ticket.requester
                    ? `${ticket.requester.firstName} ${ticket.requester.lastName}`
                    : (ticket.raisedBy ?? "—")}
                </span>
              </div>
              <div className="meta-item">
                <label>Assigned To</label>
                <span>
                  {(() => {
                    const dn = ticket.assignedAgent?.agentProfile?.displayName;
                    return Array.isArray(dn) ? (dn[0] ?? '—') : (dn ?? '—');
                  })()}
                </span>
              </div>
              <div className="meta-item">
                <label>Raised On</label>
                <span>
                  {ticket.createdAt
                    ? new Date(ticket.createdAt).toLocaleString()
                    : "—"}
                </span>
              </div>
              <div className="meta-item">
                <label>Due By</label>
                <span style={{ color: "var(--orange)", fontWeight: 700 }}>
                  {ticket.slaDueAt
                    ? new Date(ticket.slaDueAt).toLocaleString()
                    : "—"}
                </span>
              </div>
              {Array.isArray(ticket.watcherDetails) && ticket.watcherDetails.length > 0 && (
                <div className="meta-item" style={{ gridColumn: "1 / -1" }}>
                  <label>Watchers</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {ticket.watcherDetails.map((w) => (
                      <div
                        key={w.id}
                        title={w.email ?? ""}
                        style={{
                          display: "flex", alignItems: "center", gap: 5,
                          background: "#e8f0fe", border: "1px solid #c5d5f5",
                          borderRadius: 20, padding: "4px 12px", fontSize: 11.5,
                          cursor: "default"
                        }}>
                        <i className="pi pi-user" style={{ fontSize: 10, color: "#2563a8" }} />
                        <span style={{ fontWeight: 600, color: "#1a3a6b" }}>
                          {w.firstName} {w.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </EssCard>


          {/* ── Pending Approval Banner ── */}
          {pendingApproval && (
            <div
              style={{
                marginBottom: 20,
                padding: "18px 20px",
                background: "#fffbeb",
                border: "2px solid #f59e0b",
                borderRadius: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <i className="pi pi-exclamation-circle" style={{ fontSize: 20, color: "#b45309" }} />
                <span style={{ fontWeight: 700, fontSize: 14, color: "#92400e" }}>
                  Your Approval is Required
                </span>
              </div>
              <div style={{ fontSize: 13, color: "#78350f", marginBottom: 14, lineHeight: 1.6 }}>
                {pendingApproval.message}
              </div>
              <InputTextarea
                value={approvalReply}
                onChange={(e) => setApprovalReply(e.target.value)}
                rows={2}
                placeholder="Add a comment (optional)…"
                style={{ width: "100%", marginBottom: 10 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  label="Approve"
                  icon="pi pi-check"
                  loading={respondingApproval}
                  style={{ background: "#22a06b", border: "none" }}
                  onClick={() => onRespondApproval("approved")}
                />
                <Button
                  label="Reject"
                  icon="pi pi-times"
                  loading={respondingApproval}
                  severity="danger"
                  onClick={() => onRespondApproval("rejected")}
                />
              </div>
            </div>
          )}

          <EssCard title="Conversation" icon="pi-comments">
            <CommentThread comments={comments} />
            <Divider />

            {!canReply ? (
              <div
                style={{
                  padding: 12,
                  background: "#f5f7fa",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--muted)",
                  }}
                >
                  This ticket is closed
                </div>
                <div
                  style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}
                >
                  You cannot reply to this conversation anymore.
                </div>
              </div>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    setPendingFiles((p) => [
                      ...p,
                      ...Array.from(e.target.files),
                    ]);
                    e.target.value = "";
                  }}
                />

                {pendingFiles.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      marginBottom: 8,
                    }}
                  >
                    {pendingFiles.map((f, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          background: "var(--light)",
                          border: "1px solid var(--border)",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: 11,
                        }}
                      >
                        <i
                          className="pi pi-file"
                          style={{ fontSize: 10, color: "var(--blue)" }}
                        />
                        <span>{f.name}</span>
                        <i
                          className="pi pi-times"
                          style={{
                            fontSize: 9,
                            color: "var(--red)",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setPendingFiles((p) =>
                              p.filter((_, idx) => idx !== i),
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div
                  style={{ display: "flex", gap: 10, alignItems: "flex-end" }}
                >
                  <Avatar
                    label="ME"
                    shape="circle"
                    style={{
                      background: "var(--blue)",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  />
                  <InputTextarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    style={{ flex: 1 }}
                    placeholder="Type your reply…"
                  />
                  <Button
                    icon="pi pi-paperclip"
                    outlined
                    severity="secondary"
                    size="small"
                    onClick={() => fileInputRef.current.click()}
                  />
                  <Button
                    label="Send"
                    icon="pi pi-send"
                    loading={sending}
                    style={{ background: "var(--blue)", border: "none" }}
                    onClick={onSendReply}
                  />
                </div>
              </>
            )}
          </EssCard>
        </div>



        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <EssCard title="SLA Status" icon="pi-clock">
            {["On Hold", "Pending"].includes(ticket.status) && (
              <div style={{
                marginBottom: 10, padding: "8px 12px", background: "#e0f2f1",
                border: "1px solid #80cbc4", borderRadius: 6, fontSize: 12, color: "#00695c", fontWeight: 700
              }}>
                ⏸ SLA Paused — Ticket is {ticket.status}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--muted)" }}>
                Time Remaining
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: slaColor }}>
                {sla.slaRemaining}
              </span>
            </div>
            <ProgressBar
              value={sla.slaPct}
              showValue={false}
              style={{ height: 10, borderRadius: 20 }}
              color={slaBarColor}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
              }}
            >
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                Due:{" "}
                {sla.slaDueAt ? new Date(sla.slaDueAt).toLocaleString() : "—"}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: slaColor }}>
                {sla.slaPct}% elapsed
              </span>
            </div>
            {sla.slaBreach && (
              <div
                style={{
                  marginTop: 8,
                  padding: "6px 10px",
                  background: "#ffebee",
                  borderRadius: 6,
                  fontSize: 11.5,
                  color: "var(--red)",
                  fontWeight: 700,
                }}
              >
                ⚠ SLA Breached — Immediate attention required
              </div>
            )}
          </EssCard>

          <EssCard title="Attachments" icon="pi-paperclip">
            {attachments.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                No attachments
              </div>
            )}
            {attachments.map((att) => (
              <div
                key={att.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  marginBottom: 6,
                }}
              >
                <i
                  className="pi pi-file"
                  style={{ color: "var(--red)", fontSize: 20 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {att.fileName}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    {att.fileSize}
                  </div>
                </div>
                <i
                  className="pi pi-eye"
                  style={{
                    color: "var(--green)",
                    cursor: "pointer",
                    marginRight: 8,
                  }}
                  onClick={() => onView(att)}
                />
                <i
                  className="pi pi-download"
                  style={{ color: "var(--blue)", cursor: "pointer" }}
                  onClick={() => onDownload(att)}
                />
              </div>
            ))}
          </EssCard>

          <EssCard title="Activity Log" icon="pi-history">
            <Timeline
              value={activityLog}
              content={(item) => (
                <div>
                  <div style={{ fontSize: 12 }}>{item.action}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>
                    {item.createdAt}
                  </div>
                </div>
              )}
              marker={(item) => (
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: item.color ?? "var(--blue)",
                    flexShrink: 0,
                  }}
                />
              )}
              style={{ fontSize: 12 }}
            />
          </EssCard>

          <EssCard title="Requester Info" icon="pi-user">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <Avatar
                label={
                  ticket.requester
                    ? `${ticket.requester.firstName?.[0] ?? ""}${ticket.requester.lastName?.[0] ?? ""}`.toUpperCase()
                    : (ticket.raisedBy?.substring(0, 2).toUpperCase() ?? "?")
                }
                shape="circle"
                size="large"
                style={{
                  background: "var(--blue)",
                  color: "#fff",
                  fontWeight: 700,
                }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {ticket.requester
                    ? `${ticket.requester.firstName} ${ticket.requester.lastName}`
                    : (ticket.raisedBy ?? "—")}
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>
                  {ticket.requester?.email ?? ticket.raisedByEmail ?? "—"}
                </div>
              </div>
            </div>
            {[
              [
                "Department",
                ticket.requester?.Department?.title ?? ticket.raisedByDept,
              ],
              [
                "Designation",
                ticket.requester?.Designation?.title ??
                ticket.raisedByDesignation,
              ],
              ["Tickets Raised", ticket.ticketsRaised ?? "—"],
              [
                "Last Ticket",
                ticket.lastTicketAt
                  ? new Date(ticket.lastTicketAt).toLocaleString()
                  : "—",
              ],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  fontSize: 11.5,
                  color: "var(--muted)",
                  marginBottom: 4,
                }}
              >
                {k}: <strong style={{ color: "#1a2a3a" }}>{v ?? "—"}</strong>
              </div>
            ))}
          </EssCard>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
